import * as z from 'zod';
import { optional, timestampSchema } from './common';

// Merchant Employee Service — the merchant's own staff roster, documented in
// `apps/merchant-panel/openapi-merchant-profile.json` ("Noor — Merchant Profile
// (merchant)", v1.00.294). A SEPARATE spec file from the Eats one: the service
// is part of merchant-profile, not the Eats gateway, but it shares the same
// base URL and envelope shapes, so it lives here alongside the other clients.
//
// The MERCHANT contour, not the admin one the panel was first written against
// — `employee-api.ts` lists what the two differ on.
//
// The identity itself belongs to auth-service (phone, role and password live
// there); this service only binds an identity to a brand. That split is why
// there is no "create employee" in the usual sense — see `employee-api.ts`.

/**
 * The staff roles auth-service names (`v1NewIdentity.role`, whose spec title is
 * `merchant-admin | manager | cashier`). Lower-case hyphenated strings, NOT the
 * SCREAMING_SNAKE protobuf enums the Eats services use.
 *
 * These are the roles we RENDER, not a wire constraint: `v1Employee.role` is
 * typed a bare `string`, so the schema below stays a plain string and a novel
 * server role degrades to its raw value instead of failing the roster parse.
 */
export const KNOWN_EMPLOYEE_ROLES = ['merchant-admin', 'manager', 'cashier'] as const;
export type KnownEmployeeRole = (typeof KNOWN_EMPLOYEE_ROLES)[number];

/** `v1Employee.role` — an open string on the wire; see `KNOWN_EMPLOYEE_ROLES`. */
export const employeeRoleSchema = z.string();
export type EmployeeRole = z.infer<typeof employeeRoleSchema>;

/**
 * The roles a merchant may ASSIGN from this panel.
 *
 * `merchant-admin` is deliberately absent. The spec states it outright on
 * `v1NewIdentity`: "мерчант-контур не может завести merchant-admin" — who may
 * grant which role is decided by merchant-profile BEFORE it calls auth, and
 * the merchant contour may not mint an admin. Offering it in the picker would
 * surface an option every submit rejects.
 */
export const ASSIGNABLE_EMPLOYEE_ROLES = ['manager', 'cashier'] as const;
export type AssignableEmployeeRole = (typeof ASSIGNABLE_EMPLOYEE_ROLES)[number];

/** `v1Employee` — one staff member's binding to a merchant. */
export const employeeSchema = z.object({
  employee_id: z.string(),
  merchant_id: z.string().optional(),
  phone: z.string().catch(''),
  role: employeeRoleSchema.catch(''),
  status: z.string().catch(''),
  // Both optional on the wire: an identity may be attached with no name at all
  // (`v1NewIdentity` documents the pair as optional, filled in later through
  // UpdateEmployee), and protobuf-JSON omits empty strings entirely.
  first_name: z.string().catch(''),
  last_name: z.string().catch(''),
  created_at: optional(timestampSchema),
  updated_at: optional(timestampSchema),
});
export type Employee = z.infer<typeof employeeSchema>;

/**
 * `v1AttachEmployeeResponse` — the attached employee, in full.
 *
 * The spec is explicit that this is never empty: a caller who attached by
 * PHONE does not know the `employee_id`, and assigning branches is the very
 * next step. Idempotent — re-attaching an already-bound identity returns the
 * existing row with the same shape.
 */
export const attachEmployeeResponseSchema = z.object({
  employee: employeeSchema,
});
export type AttachEmployeeResult = z.infer<typeof attachEmployeeResponseSchema>;
